// Why a call didn't book (BL-003), on the call's card: its reason, who took it, and the picker. The
// person who took the call says why; an owner or manager can review it, and after that it's theirs
// (the rate is bonused on, so the taker isn't the last word). A reason suggested from the call (a
// transcript, BL-001) waits for the taker's yes. Under a minute, nothing to say: it's excused.

import { useEffect, useRef, useState } from 'react';
import { RpcError } from '@flowology/client';
import { client } from '../api.ts';
import { SaveButton } from '../shared/SaveButton.tsx';
import type { Call } from '../types.ts';
import { callWrite, reasonMeans, reviewsCalls, type CallReason } from './reasons-model.ts';
import { useMirror } from '../shared/mirror.ts';

const say = (e: unknown) => (e instanceof RpcError ? e.message : 'Could not save. Try again.');

export function CallReasonPart({
  call,
  held,
  onCall,
  toast,
  pickSignal = 0,
  pinned = false,
}: {
  call: Call;
  held: readonly string[];
  onCall: (c: Call) => void;
  toast: (words: string) => void;
  /** Bumped by the call's pinned buttons (BL-099): open the picker for why it didn't book. */
  pickSignal?: number;
  /** "Not booked…" sits in the call's pinned buttons, not here. */
  pinned?: boolean;
}) {
  const [reasons, setReasons] = useState<CallReason[] | null>(null);
  const [picking, setPicking] = useState<'taker' | 'review' | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reviewer = reviewsCalls(held);
  // recording a call's reason is an edit: someone who may only see calls isn't offered it (QA-773)
  const records = held.includes('customers.edit');
  const mirroring = useMirror();
  useEffect(() => {
    void client
      .call<{ reasons: CallReason[] }>('callReasons.list', {})
      .then((r) => setReasons(r.reasons))
      .catch(() => setReasons([]));
  }, []);
  useEffect(() => {
    setPicking(null);
    setNote('');
    setError(null);
  }, [call.id]);
  const pickRef = useRef<HTMLDivElement>(null);
  // the pinned "Not booked…" bumps pickSignal: only a bump opens the reasons, never the count this part
  // mounts with (it mounts again for each call opened), and they come into view
  const signalSeen = useRef(pickSignal);
  const fromPin = useRef(false);
  useEffect(() => {
    if (pickSignal === signalSeen.current) return;
    signalSeen.current = pickSignal;
    fromPin.current = true;
    if (records) setPicking('taker');
  }, [pickSignal, records]);
  useEffect(() => {
    if (!picking || !fromPin.current) return;
    fromPin.current = false;
    pickRef.current?.scrollIntoView({ block: 'nearest' });
  }, [picking]);
  const booked = call.outcome === 'booked';
  const lockedForMe = !!call.reviewed && !reviewer;
  const suggested = call.suggestedReasonId
    ? reasons?.find((r) => r.id === call.suggestedReasonId)
    : undefined;

  const choose = async (r: CallReason) => {
    setBusy(r.id);
    setError(null);
    try {
      if (picking === 'review') {
        await client.call('calls.reviewReason', {
          callId: call.id,
          reasonId: r.id,
          ...(note.trim() ? { note: note.trim() } : {}),
        });
        onCall(await client.call<Call>('calls.get', { id: call.id }));
        toast(`Reviewed: ${r.name}`);
      } else {
        // what this screen saw: someone else's reason since is a clash, never silently replaced (QA-773)
        const seen = { outcome: call.outcome, reasonId: call.reason?.id ?? null };
        onCall(await client.call<Call>(callWrite(mirroring), { id: call.id, reasonId: r.id, seen }));
        toast(`Not booked: ${r.name}`);
      }
      setPicking(null);
      setNote('');
    } catch (e) {
      setError(say(e));
      // a clash: the call as it is now, so choosing again replaces it on purpose
      if (e instanceof RpcError && e.code === 'conflict')
        void client
          .call<Call>('calls.get', { id: call.id })
          .then(onCall)
          .catch(() => undefined);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="call-reason">
      <dl className="kv">
        <dt>Outcome</dt>
        <dd>
          {booked ? (
            'Booked'
          ) : call.reason ? (
            <>
              Not booked: <b>{call.reason.name}</b>
              <span className="muted small block">{reasonMeans(call.reason)}</span>
            </>
          ) : call.outcome === 'not_booked' ? (
            'Not booked, no reason yet'
          ) : call.handledOn ? (
            'Handled on their later call'
          ) : (
            'Not set yet'
          )}
          {!booked && call.underAMinute && (
            <span className="chip say block-chip">Excused: under a minute</span>
          )}
        </dd>
        <dt>Taken by</dt>
        <dd>{call.takenBy ?? 'Nobody yet'}</dd>
        {call.reviewed && (
          <>
            <dt>Reviewed</dt>
            <dd>
              {/* a reviewed call that booked later keeps its review (actions' review, L3) */}
              {booked
                ? 'Booked after review'
                : call.reviewed.changed
                  ? 'Changed in review'
                  : 'Kept in review'}
              {call.reviewed.note && <span className="muted small block">“{call.reviewed.note}”</span>}
            </dd>
          </>
        )}
      </dl>
      {suggested && !booked && !lockedForMe && records && (
        <p className="call-suggested">
          Suggested from the call: <b>{suggested.name}</b>{' '}
          <SaveButton
            className="btn soft"
            busy={busy === suggested.id}
            busyLabel="Saving…"
            onClick={() => void choose(suggested)}
          >
            That’s right
          </SaveButton>
        </p>
      )}
      {lockedForMe && !booked ? (
        <p className="muted small">
          An owner or manager reviewed this call’s reason, so it’s theirs to change.
        </p>
      ) : (
        !booked &&
        !picking && (
          <div className="row">
            {!pinned && records && (
              <button type="button" className="btn" onClick={() => setPicking('taker')}>
                {call.reason ? 'Change the reason…' : 'Not booked…'}
              </button>
            )}
            {reviewer && call.outcome === 'not_booked' && (
              <button type="button" className="btn quiet" onClick={() => setPicking('review')}>
                Review the reason…
              </button>
            )}
          </div>
        )
      )}
      {error && !picking && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {picking && (
        <div
          ref={pickRef}
          className="reason-pick"
          role="group"
          aria-label={picking === 'review' ? 'Review: why it didn’t book' : 'Why it didn’t book'}
        >
          {picking === 'review' && (
            <label className="f">
              Why (optional, kept with the review)
              <input
                className="field"
                value={note}
                maxLength={1000}
                onChange={(e) => setNote(e.target.value)}
              />
            </label>
          )}
          {(reasons ?? []).map((r) => (
            <button
              key={r.id}
              type="button"
              className={`btn soft reason${call.reason?.id === r.id ? ' on' : ''}`}
              aria-pressed={call.reason?.id === r.id}
              disabled={busy !== null}
              onClick={() => void choose(r)}
            >
              <span>{busy === r.id ? 'Saving…' : r.name}</span>
              <span className="muted small">{reasonMeans(r)}</span>
            </button>
          ))}
          {error && (
            <p className="error" role="alert">
              {error}
            </p>
          )}
          <button type="button" className="btn quiet" onClick={() => setPicking(null)}>
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
